import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

export interface VolumeMarker {
  time: number; // timestamp in ms
  price: number; // candle high or low
  isBullish: boolean; // determines if marker is below (bullish) or above (bearish) the candle
  volumeRatio?: number; // optional volume ratio to display
  isSkipped?: boolean; // dimmed appearance for skipped signals
  confluenceScore?: number; // optional confluence score (0-100)
}

interface VolumeMarkerRendererData {
  x: number;
  y: number;
  isBullish: boolean;
  volumeRatio?: number;
  isSkipped?: boolean;
  confluenceScore?: number;
}

class VolumeMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: VolumeMarkerRendererData[] = [];
  private _fontSize: number = 14;

  update(data: VolumeMarkerRendererData[], fontSize: number): void {
    this._data = data;
    this._fontSize = fontSize;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledFontSize = this._fontSize * pixelRatio;

      ctx.font = `${scaledFontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const marker of this._data) {
        const x = marker.x * pixelRatio;
        const y = marker.y * pixelRatio;

        // Set alpha for skipped markers
        ctx.globalAlpha = marker.isSkipped ? 0.5 : 1.0;

        // Draw emoji based on signal direction
        const emoji = marker.isBullish ? '\u{1F525}' : '\u{1F9CA}'; // 🔥 or 🧊
        ctx.fillText(emoji, x, y);

        // Draw volume ratio text below/above the emoji if available
        if (marker.volumeRatio !== undefined) {
          const textOffset = marker.isBullish ? scaledFontSize * 0.8 : -scaledFontSize * 0.8;
          ctx.font = `${scaledFontSize * 0.6}px Arial`;
          ctx.fillStyle = marker.isSkipped ? '#888888' : '#ffffff';
          ctx.fillText(`${marker.volumeRatio.toFixed(0)}x`, x, y + textOffset);

          // Draw confluence score below volume ratio if available and > 0
          if (marker.confluenceScore !== undefined && marker.confluenceScore > 0) {
            const confluenceOffset = marker.isBullish
              ? textOffset + scaledFontSize * 0.6
              : textOffset - scaledFontSize * 0.6;
            // Color based on score: green >= 50, yellow >= 30, gray < 30
            ctx.fillStyle = marker.confluenceScore >= 50 ? '#4ade80'
              : marker.confluenceScore >= 30 ? '#fbbf24'
              : '#9ca3af';
            ctx.fillText(`C${marker.confluenceScore}`, x, y + confluenceOffset);
          }

          // Reset font for next iteration
          ctx.font = `${scaledFontSize}px Arial`;
        }
      }

      // Reset alpha
      ctx.globalAlpha = 1.0;
    });
  }
}

class VolumeMarkerPaneView implements IPrimitivePaneView {
  private _source: VolumeMarkerPrimitive;
  private _renderer: VolumeMarkerPaneRenderer;

  constructor(source: VolumeMarkerPrimitive) {
    this._source = source;
    this._renderer = new VolumeMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    // 'top' ensures markers render above candles
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.getFontSize());
    return this._renderer;
  }
}

/** Layout box of one 🔥/🧊 stack (glyph, ratio, confluence), relative to the glyph centre. */
export function volumeMarkerBox(
  fontSize: number,
  m: Pick<VolumeMarker, 'volumeRatio' | 'confluenceScore'>,
  measure: (text: string, font: string) => number,
): { w: number; h: number; glyphFromNear: number } {
  const near = fontSize * 0.6; // emoji half-height
  let far = near;
  let w = fontSize * 1.3;
  const small = `${fontSize * 0.6}px Arial`;
  if (m.volumeRatio !== undefined) {
    far = fontSize * 1.1;
    w = Math.max(w, measure(`${m.volumeRatio.toFixed(0)}x`, small));
    if (m.confluenceScore !== undefined && m.confluenceScore > 0) {
      far = fontSize * 1.7;
      w = Math.max(w, measure(`C${m.confluenceScore}`, small));
    }
  }
  return { w: Math.ceil(w) + 2, h: Math.ceil(near + far), glyphFromNear: near };
}

export class VolumeMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: VolumeMarker[] = [];
  private _paneView: VolumeMarkerPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _fontSize: number = 14; // Default emoji font size
  private _offset: number = 20; // Offset from candle high/low in pixels
  private _layout = new MarkerLayoutClient('volume', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new VolumeMarkerPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: fixed offset alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    this._markers.forEach((marker, i) => {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) return;
      const x = ctx.timeToX(t);
      const y = ctx.priceToY(marker.price);
      if (x === null || y === null) return;
      const box = volumeMarkerBox(this._fontSize, marker, ctx.measure);
      out.push({
        key: `${t}:${marker.isBullish ? 'b' : 's'}:${i}`,
        time: t,
        x,
        side: marker.isBullish ? 'below' : 'above',
        anchorY: y,
        w: box.w,
        h: box.h,
        gap: 3,
        priority: LAYOUT_PRIORITY.volume,
        color: marker.isBullish ? '#fb923c' : '#7dd3fc',
        label: `${marker.isBullish ? '🔥' : '🧊'} volume spike${marker.volumeRatio !== undefined ? ` ${marker.volumeRatio.toFixed(0)}x` : ''}`,
        ref: { marker, glyphFromNear: box.glyphFromNear },
      });
    });
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setMarkers(markers: VolumeMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setFontSize(size: number): void {
    this._fontSize = size;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setOffset(offset: number): void {
    this._offset = offset;
    this._requestUpdate?.();
  }

  getFontSize(): number {
    return this._fontSize;
  }

  getRendererData(): VolumeMarkerRendererData[] {
    if (!this._series || !this._chart) return [];

    const shared = this._layout.placed();
    if (shared) {
      return shared.map((p) => {
        const { marker, glyphFromNear } = p.item.ref as { marker: VolumeMarker; glyphFromNear: number };
        // Glyph nearest the candle, its text further out.
        const y = marker.isBullish ? p.top + glyphFromNear : p.top + p.h - glyphFromNear;
        return { x: p.item.x, y, isBullish: marker.isBullish, volumeRatio: marker.volumeRatio, isSkipped: marker.isSkipped, confluenceScore: marker.confluenceScore };
      });
    }
    const timeScale = this._chart.timeScale();
    const data: VolumeMarkerRendererData[] = [];

    for (const marker of this._markers) {
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      // Get y coordinate from price with offset
      // For bullish (below bar), we add offset to move marker down (higher y value)
      // For bearish (above bar), we subtract offset to move marker up (lower y value)
      const priceY = this._series.priceToCoordinate(marker.price);
      if (priceY === null) continue;

      // Apply pixel offset
      const y = marker.isBullish
        ? priceY + this._offset  // Below candle (lower on screen = higher y)
        : priceY - this._offset; // Above candle (higher on screen = lower y)

      data.push({
        x,
        y,
        isBullish: marker.isBullish,
        volumeRatio: marker.volumeRatio,
        isSkipped: marker.isSkipped,
        confluenceScore: marker.confluenceScore,
      });
    }

    return data;
  }
}
